import { useState } from "react";

import { CallsService } from "../services/calls.service";
import { NumberAssociationResponse } from "../types/calls.types";

// Each lookup costs 3 Zoho API credits, so results are kept for the lifetime
// of the page (keyed by raw number) - re-expanding a row, or another call to
// the same number, reuses the answer instead of spending credits again.
const ASSOCIATION_CACHE = new Map<string, NumberAssociationResponse>();

export const clearNumberAssociationCache = () => {
  ASSOCIATION_CACHE.clear();
};

export const useNumberAssociations = (rawNumber: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<NumberAssociationResponse | null>(
    () => ASSOCIATION_CACHE.get(rawNumber) ?? null,
  );

  const lookup = async () => {
    const cached = ASSOCIATION_CACHE.get(rawNumber);
    if (cached) {
      setResult(cached);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await CallsService.getNumberAssociations(rawNumber);
      ASSOCIATION_CACHE.set(rawNumber, data);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not look up number.");
    } finally {
      setLoading(false);
    }
  };

  return { lookup, loading, error, result };
};
